import {
    Component,
    EventEmitter,
    inject,
    OnDestroy,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { from, of, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskColumn } from "~app/common/components/table/models/rask-column";
import { Mattslag, MatTyper, Svar } from "~app/common/models/constants";
import { ArbetsorderInmatningshandelssortimentDTO } from "~app/common/models/planera/ArbetsorderInmatningshandelssortimentDTO";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager, ICommandManager } from "~app/common/services/cqs/commandManager";
import { EventService } from "~app/common/services/cqs/event.service";

import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { TableComponent } from "@components/table/table.component";
import {
    InitiateArbetsorderViol3SynchronizationCommand
} from "~app/features/administration/systemadministration/tabs/sysadm-viol3/commands/InitiateArbetsorderViol3SynchronizationCommand";
import { SetArbetsorderInmatningshandelssortimentJusteringCommand } from "../commands/SetArbetsorderInmatningsHandelssortimentJusteringCommand";
import { ArbetsorderService } from "../services/arbetsorder.service";

export interface ArbetsorderViol3ForstaledskontraktData {
    arbetsorderId: number;
    canEdit: boolean;
    skickadTillViol3: boolean;
}

@Component({
    selector: "rask-arbetsorder-viol3-justera-matningsinformation",
    templateUrl: "./arbetsorder-viol3-justera-matningsinformation.component.html",
    styleUrl: "./arbetsorder-viol3-justera-matningsinformation.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TableComponent,
        BusySpinnerComponent,
        ButtonComponent
    ]
})
export class ArbetsorderViol3JusteraMatningsinformationComponent  implements OnInit, OnDestroy {
    activeModal = inject(NgbActiveModal);
    arbetsorderInmatningshandelssortimentData: ArbetsorderInmatningshandelssortimentDTO[] = [];
    @Output() asyncProgressOutput = new EventEmitter<boolean>();
    data: ArbetsorderViol3ForstaledskontraktData;
    formDirty = false;
    isLoading = false;
    tableColumns: RaskColumn[];

    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly busyService = inject(BusyService);
    private readonly commandManager: ICommandManager;
    private readonly commandService = inject(CommandService);
    private readonly eventService = inject(EventService);
    private readonly ngUnsubscribe$ = new Subject<void>();

    constructor() {
        this.commandManager = new CommandManager();
    }

    canSave() {
        if (this.formDirty ) {
            return true;
        }
        return undefined;
    }

    close() {
        this.activeModal.close();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.getData();
        this.initColumns();
        this.eventService.subscribeAllArbetsorderSaga("SynchronizeVirkesorderSaga")
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event) => {
                if (event.active === false) {
                    this.getData();
                }
            });
    }

    spara() {
        this.arbetsorderInmatningshandelssortimentData.forEach(x => {
            this.commandManager.addOrUpdate(new SetArbetsorderInmatningshandelssortimentJusteringCommand(s => {
                s.InmatningshandelssortimentId = x.id;
                s.mattslagJusterad = x.mattslagJusterad;
                s.stockmatningKravsJusterad = x.stockmatningKravsJusterad;
                s.kvalitetsklassningJusterad = x.kvalitetsklassningJusterad;
                s.prioriteradMatmetodJusterad = x.prioriteradMatmetodJusterad;
                s.andraMattslagJusterad = x.andraMattslagJusterad;
            }), s => s.InmatningshandelssortimentId === x.id);
        });

        const work = from(this.commandService.pushCommands(this.commandManager));
        this.busyService
            .addWork(work)
            .subscribe(() => {
                this.updateFromViol();
                this.commandManager.clear();
                this.isLoading = true;
            });
    }

    private getData() {
        this.arbetsorderService.getArbetsorderInmatningshandelssortimentsrader(this.data.arbetsorderId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((inmatningar) => {
                if (inmatningar.length > 0) {
                    this.arbetsorderInmatningshandelssortimentData = inmatningar;
                }
                this.isLoading = false;
                this.formDirty = false;
            });
    }

    private initColumns() {
        this.tableColumns = [
            {
                headerName: "Radnummer",
                field: "radNr",
                readonly: this.isRadReadOnly,
                type: "span",
                minWidth: 60,
                maxWidth: 160,
                resizable: true,
                filter: false,
                sortable: false
            },
            {
                headerName: "Inmätt hs",
                field: "violId",
                readonly: this.isRadReadOnly,
                type: "span",
                minWidth: 60,
                resizable: true
            },
            {
                headerName: "Måttslag",
                field: "mattslagJusterad",
                readonly: (this.data.skickadTillViol3 || this.isRadReadOnly),
                editable: this.data.canEdit,
                minWidth: 60,
                maxWidth: 160,
                resizable: true,
                type: "selector",
                values: of(Mattslag.AllaViol3Mattslag),
                placeholder: (input) => (input.mattslag),
                viewFormatter: (input) => input ? input : undefined,
                formatter: (input) => input ? input : undefined,
                dataChanged: new Subject()
            },
            {
                headerName: "Andra måttslag",
                field: "andraMattslagJusterad",
                placeholder: (input) => this.parseBoolFormatter(input.andraMattslag),
                readonly: this.isRadReadOnly,
                editable: this.data.canEdit,
                minWidth: 90,
                maxWidth: 160,
                resizable: true,
                dataChanged: new Subject(),
                viewFormatter: (input) => this.parseBoolFormatter(input),
                type: "selector",
                values: [
                    Svar.ja,
                    Svar.nej
                ],
                formatter: (input) => this.parseJaNejFormatter(input)
            },
            {
                headerName: "Stockmätning krävs",
                field: "stockmatningKravsJusterad",
                readonly: this.isRadReadOnly,
                placeholder: (input) => this.parseBoolFormatter(input.stockmatningKravs),
                editable: this.data.canEdit,
                minWidth: 120,
                maxWidth: 200,
                resizable: true,
                dataChanged: new Subject(),
                type: "selector",
                values: [
                    Svar.ja,
                    Svar.nej
                ],
                viewFormatter: (input) => this.parseBoolFormatter(input),
                formatter: (input) => this.parseJaNejFormatter(input)
            },
            {
                headerName: "Kvalitetsklassning",
                field: "kvalitetsklassningJusterad",
                placeholder: (input) => this.parseBoolFormatter(input.kvalitetsklassning),
                filter: false,
                sortable: false,
                readonly: this.isRadReadOnly,
                editable: this.data.canEdit,
                minWidth: 90,
                maxWidth: 180,
                resizable: true,
                dataChanged: new Subject(),
                type: "selector",
                values: [
                    Svar.ja,
                    Svar.nej
                ],
                viewFormatter: (input) => this.parseBoolFormatter(input),
                formatter: (input) => this.parseJaNejFormatter(input)
            },
            {
                headerName: "Prioriterad mätmetod",
                readonly: this.isRadReadOnly,
                placeholder: (input) => input.prioriteradMatmetod,
                field: "prioriteradMatmetodJusterad",
                filter: false,
                sortable: false,
                editable: this.data.canEdit,
                viewFormatter: (input) => input,
                formatter: (input) => input,
                minWidth: 140,
                type: "selector",
                values: [
                    MatTyper.Stockmatning,
                    MatTyper.Travmatning
                ],
                dataChanged: new Subject()
            }
        ] as any[] as RaskColumn[];

        this.setDataChanged();
    }

    private readonly isRadReadOnly = (input: ArbetsorderInmatningshandelssortimentDTO) => !this.data?.canEdit;

    private readonly parseBoolFormatter = (input) => {
        if (input === false) {
            return Svar.nej;
        } else if (input === true) {
            return Svar.ja;
        } else {
            return input;
        }
    };

    private readonly parseJaNejFormatter = (input) => {
        if (input === Svar.ja) {
            return true;
        } else if (input === Svar.nej) {
            return false;
        }
        return input;
    };

    private setDataChanged() {
        this.tableColumns.forEach(x => {
            if (x.dataChanged) {
                x.dataChanged
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(this.setFormDirty);
            }
        });
    }

    private readonly setFormDirty = () => {
        this.formDirty = true;
    };

    private updateFromViol(): void {
        this.asyncProgressOutput.emit(true);
        const commandService = this.commandService.createInstance();

        const command = new InitiateArbetsorderViol3SynchronizationCommand(x => x.arbetsorderId = this.data.arbetsorderId);
        commandService.addTransactionCommand(command);
        commandService.pushCommandQueueTransaction().subscribe(x => {
            this.asyncProgressOutput.emit(false);
        });
    }
}
